/**
 * Status cell with badges (no row-level fade on selection changes).
 */

import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '@/providers/ToastProvider';

import { LoadingSpinner } from '@/components/ui';
import { ResentLinkBadge } from '@/features/resend-killed-drops';

import { DropStatusBadge, ScheduledBadge } from '@/features/smswiser-campaigns/components/shared';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { OptionBadges, draftOptionSummary } from './OptionBadges';
import {
  formatScheduleDateTime,
  isScheduleExecuteAfterElapsed,
} from '@/features/smswiser-campaigns/utils/scheduleTimezone';

interface StatusCellProps {
  drop: PreparedDrop;
  isSending: boolean;
  isKilling: boolean;
  returnTo?: string;
}

export const StatusCell = ({ drop, isSending, isKilling, returnTo }: StatusCellProps) => {
  const { success: toastSuccess, error: toastError } = useToast();

  const handleCopyUniqueId = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!drop.sentDropUniqueId) return;
      try {
        await navigator.clipboard.writeText(drop.sentDropUniqueId);
        toastSuccess('Copied', 'Unique ID copied to clipboard.');
      } catch {
        toastError('Copy failed', 'Could not copy to clipboard.');
      }
    },
    [drop.sentDropUniqueId, toastSuccess, toastError],
  );

  const hasSchedule = drop.executeAfter && !Number.isNaN(new Date(drop.executeAfter).getTime());
  const scheduleLabel = hasSchedule && drop.executeAfter ? formatScheduleDateTime(drop.executeAfter) : '';
  const scheduleElapsed =
    hasSchedule && drop.executeAfter ? isScheduleExecuteAfterElapsed(drop.executeAfter) : false;
  const isScheduled =
    drop.status === 'SENT' &&
    drop.executeAfter &&
    !Number.isNaN(new Date(drop.executeAfter).getTime()) &&
    !scheduleElapsed;
  const optionSummary = draftOptionSummary(drop);

  return (
    <div className="min-w-0 overflow-hidden">
      {isSending ? (
        <span className="flex items-center gap-1.5 text-xs text-primary">
          <LoadingSpinner size="xs" className="shrink-0" />
          Sending…
        </span>
      ) : isKilling ? (
        <span className="flex items-center gap-1.5 text-xs text-destructive">
          <LoadingSpinner size="xs" className="shrink-0" />
          Killing…
        </span>
      ) : drop.status === 'SENT' || drop.status === 'KILLED' ? (
        <div className="flex flex-col gap-0.5">
          {(drop.sentTitle ?? drop.titleBase) && (
            <span className="truncate text-xs font-medium text-foreground" title={drop.sentTitle ?? drop.titleBase}>
              {drop.sentTitle ?? drop.titleBase}
            </span>
          )}
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
            <DropStatusBadge
              status={drop.status === 'KILLED' ? 'Killed' : isScheduled ? 'Scheduled' : 'Sent'}
            />
            <OptionBadges
              forceSend={drop.forceSend}
              batchAmount={drop.batchAmount}
              aiOptimizationEnabled={drop.aiOptimizationEnabled}
              useOnlyNewDomains={drop.useOnlyNewDomains}
              clickIdStructure={drop.clickIdStructure}
              domainScheme={drop.domainScheme}
            />
            {drop.sentDropId != null && (
              <>
                <span className="shrink-0 text-muted-foreground">·</span>
                <span className="shrink-0 tabular-nums text-muted-foreground" title="Drop ID">
                  #{drop.sentDropId}
                </span>
              </>
            )}
            {drop.sentDropUniqueId && (
              <>
                <span className="shrink-0 text-muted-foreground">·</span>
                <button
                  type="button"
                  onClick={handleCopyUniqueId}
                  className="shrink-0 cursor-pointer rounded px-0.5 font-mono text-muted-foreground -mx-0.5 hover:text-foreground"
                  title="Click to copy Unique ID"
                >
                  {drop.sentDropUniqueId}
                </button>
                <span className="shrink-0 text-muted-foreground">·</span>
                <Link
                  to={`/drops/${encodeURIComponent(drop.sentDropUniqueId)}`}
                  state={returnTo ? { returnTo } : undefined}
                  className="shrink-0 hover:underline"
                >
                  Open
                </Link>
              </>
            )}
            <ResentLinkBadge drop={drop} returnTo={returnTo} />
          </span>
          {hasSchedule ? <ScheduledBadge scheduleLabel={scheduleLabel} isPast={scheduleElapsed} /> : null}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <span
            className="block truncate text-xs font-medium text-muted-foreground"
            title={`${drop.status}${drop.sentDropUniqueId ? ' · Open' : ''}`}
          >
            {drop.status}
            {drop.sentDropUniqueId && ' · Open'}
          </span>
          {drop.draftDropUniqueId && (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-medium text-orange-600 dark:text-orange-400"
              title={`Staged on SMSWiser (draft ${drop.draftDropUniqueId})`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />
              SMSWiser draft
            </span>
          )}
          <ResentLinkBadge drop={drop} returnTo={returnTo} />
          {optionSummary && (
            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              {optionSummary}
            </span>
          )}
          {hasSchedule && <ScheduledBadge scheduleLabel={scheduleLabel} isPast={scheduleElapsed} />}
        </div>
      )}
    </div>
  );
};
